import { useMemo, useRef, useState, type MouseEvent } from 'react'
import { extent as d3Extent } from 'd3-array'
import type { DailyAnomalyRow, DailyTrendRow } from '@/components/gcp/gcpHelpers'
import { formatAxisUsd, formatUsd, niceAxisScale } from '@/components/gcp/gcpHelpers'

interface DailySparklineProps {
  dailyRows: DailyTrendRow[]
  anomalies: DailyAnomalyRow[]
}

interface HoverState {
  x: number
  y: number
  date: string
  netCost: number
  anomaly?: DailyAnomalyRow
}

export default function DailySparkline({ dailyRows, anomalies }: DailySparklineProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<HoverState | null>(null)

  const chartData = useMemo(() => {
    const sorted = [...dailyRows].sort((a, b) => a.date.localeCompare(b.date))
    return sorted.slice(-30)
  }, [dailyRows])

  const anomalyByDate = useMemo(() => {
    const map = new Map<string, DailyAnomalyRow>()
    for (const anomaly of anomalies) {
      const key = anomaly.date.slice(0, 10)
      if (!map.has(key)) map.set(key, anomaly)
    }
    return map
  }, [anomalies])

  if (chartData.length === 0) {
    return <p className="text-sm text-gray-500">No daily spend data available.</p>
  }

  const width = 720
  const height = 280
  const margin = { top: 16, right: 16, bottom: 28, left: 56 }
  const innerWidth = width - margin.left - margin.right
  const innerHeight = height - margin.top - margin.bottom

  const costs = chartData.map((d) => d.net_cost)
  const [, maxCost = 0] = d3Extent(costs) ?? [0, 0]
  const { max: yMax, ticks: yTicks } = niceAxisScale(Math.max(maxCost, 0), 5)
  const yMin = 0
  const yRange = yMax - yMin || 1

  const xStep = chartData.length > 1 ? innerWidth / (chartData.length - 1) : 0
  const yScale = (value: number) => innerHeight - ((value - yMin) / yRange) * innerHeight

  const linePath = chartData
    .map((point, index) => {
      const x = index * xStep
      const y = yScale(point.net_cost)
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`
    })
    .join(' ')

  const areaPath = `${linePath} L ${(chartData.length - 1) * xStep} ${innerHeight} L 0 ${innerHeight} Z`

  const updateHover = (event: MouseEvent, index: number) => {
    const chart = chartRef.current
    const point = chartData[index]
    if (!chart || !point) return
    const rect = chart.getBoundingClientRect()
    const dateKey = point.date.slice(0, 10)
    setHover({
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top + 12,
      date: dateKey,
      netCost: point.net_cost,
      anomaly: anomalyByDate.get(dateKey),
    })
  }

  return (
    <div ref={chartRef} className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full max-w-full"
        role="img"
        aria-label="Daily spend for last 30 days"
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {yTicks.map((value) => {
            const y = yScale(value)
            return (
              <g key={value}>
                <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#e5e7eb" />
                <text x={-8} y={y + 4} textAnchor="end" className="fill-gray-500 text-[11px]">
                  {formatAxisUsd(value)}
                </text>
              </g>
            )
          })}

          <path d={areaPath} fill="#dbeafe" stroke="none" opacity={0.55} />
          <path d={linePath} fill="none" stroke="#2563eb" strokeWidth={1.25} />

          {chartData.map((point, index) => {
            const x = index * xStep
            return (
              <rect
                key={point.date}
                x={x - Math.max(xStep / 2, 6)}
                y={0}
                width={Math.max(xStep, 12)}
                height={innerHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={(e) => updateHover(e, index)}
                onMouseMove={(e) => updateHover(e, index)}
                onMouseLeave={() => setHover(null)}
              />
            )
          })}

          <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
          {[0, Math.floor((chartData.length - 1) / 2), chartData.length - 1].map((index) => {
            const point = chartData[index]
            if (!point) return null
            return (
              <text
                key={point.date}
                x={index * xStep}
                y={innerHeight + 16}
                textAnchor="middle"
                className="fill-gray-600 text-[11px]"
              >
                {point.date.slice(5, 10)}
              </text>
            )
          })}
        </g>
      </svg>

      {hover ? (
        <div
          className="pointer-events-none absolute z-10 min-w-[12rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
          style={{ left: hover.x, top: hover.y }}
        >
          <p className="font-medium text-gray-900">{hover.date}</p>
          <p className="mt-1 text-gray-700">Spend: {formatUsd(hover.netCost)}</p>
          {hover.anomaly ? (
            <p className="mt-1 text-red-700">
              Anomaly · {hover.anomaly.service}: {formatUsd(hover.anomaly.cost)} vs{' '}
              {formatUsd(hover.anomaly.baseline_avg)} baseline ({hover.anomaly.ratio.toFixed(1)}×)
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
